<template>
  <Layout>
    <sales-plan-load-error-dialog
        ref="salesPlanLoadErrorDialog"
        @back="handleBack"
        @retry="retryFailedToFetchModal"
    />
    <timeout-request-modal
        v-model="timeoutRequestModalShow"
        @back="handleBack"
        @retry="retryTimeoutRequestModal"
    />
    <Loader :loading="loading" no-axios>
      <div v-if="(model || {}).id">
        <div class="row">
          <div class="col-12">
            <a @click="$router.push('/sales_plans')">
              <div class="container-title d-flex align-items-center">
                          <span class="me-2">
                            <svg fill="none" height="16" viewBox="0 0 16 16" width="16"
                                 xmlns="http://www.w3.org/2000/svg">
                              <path
                                  d="M6.875 15.3001L0.275 8.70008C0.175 8.60008 0.104 8.49175 0.0619998 8.37508C0.0206665 8.25842 0 8.13342 0 8.00008C0 7.86675 0.0206665 7.74175 0.0619998 7.62508C0.104 7.50842 0.175 7.40008 0.275 7.30008L6.875 0.700082C7.05833 0.516749 7.28733 0.420749 7.562 0.412083C7.83733 0.404083 8.075 0.500082 8.275 0.700082C8.475 0.883416 8.57933 1.11242 8.588 1.38708C8.596 1.66242 8.5 1.90008 8.3 2.10008L3.4 7.00008H14.575C14.8583 7.00008 15.096 7.09575 15.288 7.28708C15.4793 7.47908 15.575 7.71675 15.575 8.00008C15.575 8.28342 15.4793 8.52075 15.288 8.71208C15.096 8.90408 14.8583 9.00008 14.575 9.00008H3.4L8.3 13.9001C8.48333 14.0834 8.57933 14.3167 8.588 14.6001C8.596 14.8834 8.5 15.1167 8.3 15.3001C8.11667 15.5001 7.88333 15.6001 7.6 15.6001C7.31667 15.6001 7.075 15.5001 6.875 15.3001Z"
                                  fill="#6C7589"/>
                            </svg>
                          </span>
                {{ `${$t('Detail')} ${$t("sales_plans.singular")}` }}
              </div>
            </a>
          </div>
          <div class="col-md-12 col-lg-8">
            <b-card v-bind="cardAttrs">
              <div class="row">
                <div class="col-md-6 d-flex align-items-center">
                  <h5 class="approval-history-header mb-0">
                    {{ lodash.startCase($t('sales_plans.general_information')) }}
                  </h5>
                </div>
                <div class="col-md-6 d-flex justify-content-end gap-2">
                  <b-button
                      v-if="showDuplicateButton"
                      :disabled="duplicateLoading"
                      variant="outline-primary"
                      @click="handleDuplicate(model)"
                  >
                    <b-spinner v-if="duplicateLoading" class="me-2" small></b-spinner>
                    <i class="fas fa-copy me-1"/>
                    {{ lodash.startCase($t('sales_plans.buttons.duplicate')) }}
                  </b-button>
                  <b-button
                      v-if="showEditButton"
                      variant="warning"
                      @click="handleEdit"
                  >
                    {{ lodash.startCase($t('sales_plans.buttons.edit')) }}
                  </b-button>
                  <b-button
                      v-if="showDeleteButton"
                      variant="danger"
                      @click="openDeleteConfirmModal"
                  >{{ lodash.startCase($t('sales_plans.buttons.delete')) }}
                  </b-button>
                  <b-button
                      v-if="showCancelButton"
                      variant="outline-danger"
                      @click="handleCancel"
                  >
                    {{ lodash.startCase($t('sales_plans.buttons.cancel')) }}
                  </b-button>
                  <b-button
                      v-if="showRejectButton"
                      :disabled="rejecting"
                      variant="danger"
                      @click="handleReject"
                  >
                    <b-spinner v-if="rejecting" class="me-2" small></b-spinner>
                    {{ lodash.startCase($t('sales_plans.buttons.reject')) }}
                  </b-button>
                  <b-button
                      v-if="showApproveButton"
                      :disabled="approving"
                      variant="success"
                      @click="handleApprove"
                  >
                    <b-spinner v-if="approving" class="me-2" small></b-spinner>
                    {{ lodash.startCase($t('sales_plans.buttons.approve')) }}
                  </b-button>
                </div>
              </div>
              <hr>
              <div class="row">
                <div class="col-md-4 d-flex flex-column gap-3">
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{
                        `${lodash.upperCase($t(`${this.resourceRoute}.id`))} ${lodash.startCase($t(`${this.resourceRoute}.singular`))}`
                      }}
                    </div>
                    <div class="text-neutral-600">
                      {{ model.name || "-" }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ `${$t(`${this.resourceRoute}.ToP`)} (${lodash.lowerCase($t('Days'))})` }}
                    </div>
                    <div class="text-neutral-600">
                      {{
                        model.term_of_payment
                      }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t(`${this.resourceRoute}.product`)) }}
                    </div>
                    <div class="text-neutral-600">
                      {{ extractProductName(model.product) }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t(`${this.resourceRoute}.partner`)) }}
                    </div>
                    <div class="text-neutral-600">
                      {{ extractPartnerName(model.partner) }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t(`${this.resourceRoute}.status`)) }}
                    </div>
                    <div class="text-neutral-600">
                      <status-badge
                          v-if="model.status"
                          :status="model.status"
                          entity="sales_plan"
                          type="status"
                      ></status-badge>
                      <span v-else>-</span>
                    </div>
                  </div>
                </div>
                <div class="col-md-4 d-flex flex-column gap-3">
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t(`${this.resourceRoute}.project_period`)) }}
                    </div>
                    <div class="text-neutral-600">
                      {{
                        `${$moment(model.period_start).format("DD MMM YYYY")} - ${$moment(model.period_end).format("DD MMM YYYY")}`
                      }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t(`${this.resourceRoute}.published_date`)) }}
                    </div>
                    <div class="text-neutral-600">
                      {{ publishedDate }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t(`${this.resourceRoute}.farm`)) }}
                    </div>
                    <div class="text-neutral-600">
                      {{ extractFarmName(model.farm) }}
                    </div>
                  </div>
                  <div class="description-detail">
                    <div class="label font-size-500">
                      {{ lodash.startCase($t(`models.created_by`)) }}
                    </div>
                    <div class="text-neutral-600">
                      {{ extractCreatorName(model.creator) }}
                    </div>
                  </div>
                </div>
                <div class="col-12">
                  <b-card class="mt-3" no-body>
                    <b-table-simple borderless class="mb-0">
                      <b-thead>
                        <b-tr>
                          <b-th>{{ lodash.startCase($t('sales_plans.estimated_size')) }}</b-th>
                          <b-th>{{ lodash.startCase($t('sales_plans.estimated_quantity')) }}</b-th>
                          <b-th>{{ lodash.startCase($t('sales_plans.estimated_sale')) }}</b-th>
                          <b-th>{{ lodash.startCase($t('sales_plans.realization_amount')) }}</b-th>
                          <b-th>{{ lodash.startCase(`${$t('sales_plans.realization')} (%)`) }}</b-th>
                          <b-th>{{ lodash.startCase($t('sales_plans.estimated_margin')) }}</b-th>
                          <b-th>{{ lodash.startCase(`${$t('sales_plans.estimated_margin')} (%)`) }}</b-th>
                        </b-tr>
                      </b-thead>
                      <b-tbody>
                        <b-tr>
                          <b-td>{{
                              `${decimalFormatter(model.estimated_size_start)} - ${decimalFormatter(model.estimated_size_end)}`
                            }}
                          </b-td>
                          <b-td>{{ decimalFormatter(model.estimated_quantity) }}</b-td>
                          <b-td>{{ currencyFormatter(model.estimated_sale) }}</b-td>
                          <b-td>{{ currencyFormatter(model.realized_amount) }}</b-td>
                          <b-td>{{
                              model.realized_amount_ratio != null ? percentageFormatter(model.realized_amount_ratio) : "-"
                            }}
                          </b-td>
                          <b-td>{{ currencyFormatter(model.estimated_margin) }}</b-td>
                          <b-td>{{
                              model.estimated_margin_ratio != null ? percentageFormatter(model.estimated_margin_ratio) : "-"
                            }}
                          </b-td>
                        </b-tr>
                      </b-tbody>
                    </b-table-simple>
                  </b-card>
                </div>
                <div class="col-12">
                  <b-card>
                    <h5 class="approval-history-header mb-2">{{
                        lodash.startCase($t('sales_plans.processing_data'))
                      }}</h5>
                    <div class="row">
                      <div class="col-sm-12 col-md-6">
                        <div class="processing-information">
                          {{ extractProcessingCode(model.processing) }}
                          {{ extractProcessingName(model.processing) }}
                        </div>
                      </div>
                      <div class="col-sm-12 col-md-6 text-end">
                        <div class="processing-information">
                          <span class="pe-2" style="font-weight: 700;">{{ lodash.startCase($t('sales_plans.dpd_status')) }}</span>
                          <b-badge
                              :variant="daysPastDueVariant(processingMaxDaysPastDueIn90Days)"
                              class="processing-dpd-badge"
                          >
                            {{ formattedDaysPastDue(processingMaxDaysPastDueIn90Days) }}
                          </b-badge>
                        </div>
                      </div>
                    </div>
                    <div class="pbarik-progress-section">
                      <div class="d-flex justify-content-between align-items-start mb-2">
                        <div>
                          <div class="processing-limit-progress-meta">
                            {{ lodash.upperFirst($t('sales_plans.used')) }}:
                            <b style="font-weight: 700; color: #1B91FF">{{
                                currencyFormatter(processingUsedAmount)
                              }}</b>
                          </div>
                        </div>
                        <div class="processing-limit-progress-meta text-end">
                          {{ lodash.upperFirst($t('sales_plans.available')) }}:
                          <b style="font-weight: 700; color: #22C55E">{{
                              currencyFormatter(processingAvailableAmount)
                            }}</b>
                        </div>
                      </div>
                      <b-progress
                          :max="100"
                          :value="processingUsedAmountProgressPercentage"
                          height="10px"
                          variant="primary"
                      ></b-progress>
                      <div class="d-flex justify-content-end align-items-start mb-2">
                        <div class="processing-limit-progress-meta">
                          {{
                            `${lodash.upperFirst($t('sales_plans.limit_amount'))}: ${currencyFormatter(processingLimitAmount)}`
                          }}
                        </div>
                      </div>
                    </div>
                  </b-card>
                </div>
              </div>
              <b-card-text class="change-history-card-text">
                <div class="change-history-title">{{ lodash.startCase($t(`${resourceRoute}.change_history`)) }}</div>
                <div class="mb-1">
                  <ul id="activity-log-list" class="mb-0" style="margin-left: -12px">
                    <li
                        v-for="(formattedActivityLog, index) in formattedActivityLogsToShow"
                        :key="`activity-logs-${index}`"
                        class="mt-2"
                        v-html="formattedActivityLog"
                    >
                    </li>
                  </ul>
                </div>
                <template v-if="formattedActivityLogs.length > maxActivityToShowOnCollapse">
                  <b-link
                      class="activity-log-collapse"
                      href="#"
                      @click.prevent="openActivityLogModal"
                  >
                    {{ lodash.startCase($t('See All')) }}
                  </b-link>
                </template>
              </b-card-text>
            </b-card>
          </div>
          <div class="col-md-12 col-lg-4">
            <b-card>
              <div class="col-12">
                <h5 class="approval-history-header mb-0">{{
                    lodash.startCase($t('sales_plans.approval_history'))
                  }}</h5>
              </div>
              <hr>
              <template v-for="(step, index) in approvalProgressSteps">
                <approval-progress-step
                    :key="approvalStepKey(step, index)"
                    :is-last="index === approvalProgressSteps.length - 1"
                    :step="step"
                />
              </template>
            </b-card>
          </div>
          <div v-if="model.status === 'approved'" class="col-md-12">
            <b-card>
              <sales-plan-relation-tabs v-model="activeRelationTab" class="mb-3"/>
              <purchase-orders
                  v-if="activeRelationTab === 'purchase_orders'"
                  :parent="model"
                  :create-route="{ name: 'purchase_orders.create', query: { mode: 'sales_plan', sales_plan_id: model.id } }"
              />
              <trips
                  v-else-if="activeRelationTab === 'trips'"
                  :parent="model"
                  :create-route="{ name: 'trips.create' }"
              />
              <unloads v-else-if="activeRelationTab === 'unloads'" :parent="model"/>
              <sale-notes v-else-if="activeRelationTab === 'sale_notes'" :parent="model"/>
              <balancing v-else-if="activeRelationTab === 'balancing'" :parent="model"/>
            </b-card>
          </div>
        </div>

        <b-modal
            v-model="activityLogModalShow"
            :title="lodash.startCase($t(`${resourceRoute}.change_history`))"
            centered
            hide-footer
            scrollable
            size="lg"
            title-class="activity-log-modal-title"
        >
          <div class="activity-log-modal-content">
            <ul id="activity-log-modal-list" class="mb-0 ps-3">
              <li
                  v-for="(formattedActivityLog, index) in formattedActivityLogs"
                  :key="`activity-log-modal-${index}`"
                  class="mt-2"
                  v-html="formattedActivityLog"
              >
              </li>
            </ul>
          </div>
        </b-modal>
        <b-modal
            v-model="deleteConfirmModalShow"
            centered
            footer-class="border-0 pt-0"
            hide-header
            no-close-on-backdrop
            no-close-on-esc
        >
          <div class="text-center">
            <h4>{{ `${$t('sales_plans.modal_messages.confirm_delete_title')}?` }}</h4>
            <p class="m-0" v-html="deleteConfirmDescription()"></p>
          </div>
          <template #modal-footer>
            <div class="d-flex justify-content-end gap-2 w-100">
              <b-button
                  :disabled="deleting"
                  block
                  variant="light"
                  @click="closeDeleteConfirmModal"
              >
                <b>{{ $t('Cancel') }}</b>
              </b-button>
              <b-button
                  :disabled="deleting"
                  block
                  class="m-0"
                  variant="danger"
                  @click="confirmDelete"
              >
                <b-spinner v-if="deleting" class="me-2" small></b-spinner>
                <b>{{ $t('Delete') }}</b>
              </b-button>
            </div>
          </template>
        </b-modal>
        <b-modal
            v-model="duplicateConfirmModalShow"
            centered
            footer-class="border-0 pt-0"
            hide-header
            no-close-on-backdrop
            no-close-on-esc
        >
          <div class="text-center">
            <h4>{{ duplicateConfirmTitle() }}?</h4>
            <p class="m-0">{{ duplicateConfirmDescription() }}</p>
          </div>
          <template #modal-footer>
            <div class="d-flex justify-content-end gap-2 w-100">
              <b-button
                  :disabled="duplicateLoading"
                  block
                  variant="light"
                  @click="closeDuplicateConfirmModal"
              >
                {{ $t('Cancel') }}
              </b-button>
              <b-button
                  :disabled="duplicateLoading"
                  block
                  class="m-0"
                  variant="primary"
                  @click="confirmDuplicate"
              >
                <b-spinner v-if="duplicateLoading" class="me-2" small></b-spinner>
                {{ lodash.startCase($t('sales_plans.buttons.continue_duplicate')) }}
              </b-button>
            </div>
          </template>
        </b-modal>

        <sales-plan-approve-confirmation
            ref="sales-plan-approve-confirmation"
            :sales-plan="this.model"
        />
        <sales-plan-reject-confirmation
            ref="sales-plan-reject-confirmation"
            :sales-plan="this.model"
        />
        <sales-plan-cancel-confirmation
            ref="sales-plan-cancel-confirmation"
            :sales-plan="this.model"
            @cancelled="onCancelled"
        />
      </div>
    </Loader>
  </Layout>
</template>

<script>
import ModelShow from "../models/show";
import Row from "@/components/models/show/custom-row";
import JalaAppIcon from '@/assets/icons/JalaApp.svg'
import JalaHarvestIcon from '@/assets/icons/JalaHarvest.svg'
import JalaProIcon from '@/assets/icons/JalaPro.svg'
import Loader from '@/components/widgets/loader'
import SalesPlanLoadErrorDialog from '@/components/modals/sales-plan-load-error-dialog.vue'
import TimeoutRequestModal from '@/router/views/purchase_orders/modals/TimeoutRequestModal.vue'
import SalesPlanApproveConfirmation from '@/components/modals/sales-plan-approve-confirmation.vue'
import SalesPlanRejectConfirmation from '@/components/modals/sales-plan-reject-confirmation.vue'
import SalesPlanCancelConfirmation from '@/components/modals/sales-plan-cancel-confirmation.vue'
import ApprovalProgressStep from '@/components/sales-plans/approval-progress-step.vue'
import salesPlanDuplicate from "@/components/mixins/sales-plan-duplicate";
import {currencyFormatter, decimalFormatter, percentageFormatter} from "@/helpers/ui/formatters";
import {daysPastDueVariant} from "@/helpers/ui/badgeVariants";
import {hasAllowedAction} from "@/helpers/ui/allowedActions";
import {serialize} from 'object-to-formdata'
import Swal from 'sweetalert2'
import PurchaseOrders from "./purchase_orders/index.vue";
import Trips from "./trips/index.vue";
import Unloads from "./unloads/index.vue";
import SaleNotes from "./sale_notes/index.vue";
import SalesPlanRelationTabs from "./show/relation-tabs.vue";
import Balancing from "@/router/views/sales_plans/balancing/index.vue";

export default {
  extends: ModelShow,
  mixins: [salesPlanDuplicate],
  page: {
    title: `Detail Sales Plan`,
  },
  components: {
    Balancing,
    Loader,
    SalesPlanLoadErrorDialog,
    TimeoutRequestModal,
    SalesPlanApproveConfirmation,
    SalesPlanRejectConfirmation,
    SalesPlanCancelConfirmation,
    ApprovalProgressStep,
    PurchaseOrders,
    Trips,
    Unloads,
    SaleNotes,
    SalesPlanRelationTabs,
  },
  props: {
    cardTitle: {
      default() {
        return this.lodash.startCase(this.$t("sales_plans.singular"));
      },
    },
    resourceRoute: {default: "sales_plans"},
    modelVariable: {default: "sales_plan"},
    denyEdit: {type: Boolean, default: true},
    denyDelete: {type: Boolean, default: true},
    columns: {
      type: Object,
      default() {
        return {
          [this.modelVariable || "model"]: [
            {
              key: "key",
              label: this.$t("models.key"),
              component: Row,
            },
          ],
        };
      },
    },
  },
  data() {
    return {
      title: this.lodash.startCase(this.$t("sales_plans.plural")),
      lineFields: ['label', 'quantity', 'UoM', 'price_unit', 'price_subtotal'],
      JalaAppIcon,
      JalaHarvestIcon,
      JalaProIcon,
      maxActivityToShowOnCollapse: 5,
      activity_logs: [],
      activityLogModalShow: false,
      deleteConfirmModalShow: false,
      duplicateConfirmModalShow: false,
      deleting: false,
      duplicateLoading: false,
      approving: false,
      rejecting: false,
      activeRelationTab: "purchase_orders",
      previousRoute: null,
      timeoutRequestModalShow: false,
    };
  },
  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.previousRoute = from.matched.length ? from : null;
    });
  },
  mounted() {
    this.fetchData();
  },
  watch: {
    '$route.params.key'(newKey, oldKey) {
      if (newKey && newKey !== oldKey) {
        this.fetchData();
      }
    },
  },
  computed: {
    approvalProgressSteps() {
      const steps = this.model?.approval_progress?.steps ?? []

      const groupedSteps = steps.reduce((accumulator, step, index) => {
        const level = step?.level ?? step?.approver?.level ?? index

        if (!accumulator[level]) {
          accumulator[level] = []
        }

        accumulator[level].push(step)
        return accumulator
      }, {})

      return Object.keys(groupedSteps)
          .sort((levelA, levelB) => Number(levelA) - Number(levelB))
          .map((level) => this.pickApprovalProgressStep(groupedSteps[level]))
    },
    processingMaxDaysPastDueIn90Days() {
      return this.model?.processing?.odoo_res_partner?.max_days_past_due_in_90_days
    },
    processingLimitGroup() {
      return this.model?.processing?.odoo_res_partner?.odoo_obligor_limit?.odoo_obligor_limit_group ?? null
    },
    processingLimitAmount() {
      return Number(this.processingLimitGroup?.limit_amount ?? 0)
    },
    processingUsedAmount() {
      return Number(this.processingLimitGroup?.summary?.adjusted_used_amount ?? 0)
    },
    processingAvailableAmount() {
      return Number(this.processingLimitGroup?.summary?.adjusted_available_amount ?? 0)
    },
    processingUsedAmountProgressPercentage() {
      const usedAmountRatio = Number(this.processingLimitGroup?.summary?.adjusted_used_amount_ratio ?? 0)

      return Math.max(0, Math.min(100, usedAmountRatio * 100))
    },
    formattedActivityLogs() {
      return this.activity_logs.map(activityLog => {
        return this.formattedSalesPlanActivityLogs(activityLog);
      }).flat(1) ?? [];
    },
    formattedActivityLogsToShow() {
      return this.formattedActivityLogs.slice(0, this.maxActivityToShowOnCollapse);
    },
    publishedDate() {
      let publishedHistory = this.model.sales_plan_status_histories.find(history => history.event_type === 'publish')
      if (!publishedHistory) {
        return '-'
      }
      return this.$moment(publishedHistory.event_at).format('DD MMM YYYY')
    },
    showDeleteButton() {
      return hasAllowedAction(this.model, ['delete', 'destroy'])
    },
    showEditButton() {
      return hasAllowedAction(this.model, ['edit', 'update'])
    },
    showApproveButton() {
      return hasAllowedAction(this.model, ['approve'])
    },
    showRejectButton() {
      return hasAllowedAction(this.model, ['reject'])
    },
    showCancelButton() {
      return hasAllowedAction(this.model, ['cancel'])
    },
    showDuplicateButton() {
      return this.$auth.hasPermissionTo('sale_plans.create')
    },
  },
  methods: {
    handleBack() {
      if (this.previousRoute) {
        this.$router.push(this.previousRoute.fullPath);
        return;
      }

      this.$router.push({name: "sales_plans.index"});
    },
    currencyFormatter,
    decimalFormatter,
    percentageFormatter,
    daysPastDueVariant,
    formattedDaysPastDue(daysPastDue) {
      return daysPastDue || '-'
    },
    approvalStepKey(step, index) {
      if (step?.id) {
        return `approval-step-${step.id}`
      }

      const level = step && step.level !== undefined && step.level !== null
          ? step.level
          : index

      return `${level}-step-details`
    },
    pickApprovalProgressStep(steps) {
      const prioritizedStatuses = ['rejected', 'approved', 'current', 'waiting', 'pending']

      for (const status of prioritizedStatuses) {
        const matchedStep = steps.find((step) => step?.status === status)
        if (matchedStep) {
          return matchedStep
        }
      }

      return steps[0]
    },
    deleteSalesPlan() {
      return this.$axios.delete(`/v1/sales-plans/${this.$route.params.key}`)
    },
    duplicateConfirmTitle() {
      return this.$t('sales_plans.modal_messages.confirm_duplicate_title')
    },
    duplicateConfirmDescription() {
      return this.$t('sales_plans.modal_messages.confirm_duplicate_description')
    },
    extractCreatorName(creator) {
      return creator?.name ?? '-'
    },
    extractFarmName(farm) {
      return farm?.name ?? '-'
    },
    extractProcessingCode(processing) {
      return processing?.code ?? '-'
    },
    extractProcessingName(processing) {
      return processing?.name ?? '-'
    },
    extractProductName(product) {
      return product?.name ?? '-'
    },
    extractPartnerName(partner) {
      return partner?.name ?? '-'
    },
    formattedSalesPlanActivityLogs(salesPlanActivityLog) {
      const {description, properties, causer_name} = salesPlanActivityLog
      let showKeys = [
        'period_start',
        'period_end',
        'term_of_payment',
        'product_name',
        'partner_name',
        'farm_name',
        'processing_name',
        'estimated_size_start',
        'estimated_size_end',
        'estimated_quantity',
        'estimated_sale',
        'estimated_margin',
      ]
      const createdText = this.$t('created')
      const byText = this.$t('by')

      if (description === 'cancelled') {
        return [`<span class="me-2" style="color: #667085">${this.$moment.utc(salesPlanActivityLog.created_at).local().format('DD-MM-YYYY HH:mm')}</span>${this.lodash.startCase(this.$t('sales_plans.singular'))} <b style="color: #000">${this.$t('cancelled')}</b> <span style="color:#667085">${byText} ${causer_name}</span>`]
      }

      if (description === 'updated') {
        let keys = Object.keys(properties.old)
        return keys.flatMap((key) => {
          if (!showKeys.includes(key)) {
            return []
          }
          const {old, attributes} = properties
          let oldValue = old[key]
          let newValue = attributes[key]

          if (oldValue === newValue) {
            return null
          }

          let needToFormatInCurrencyKeys = ['estimated_sale', 'estimated_margin']
          let needToFormatInDecimalKeys = ['estimated_size_start', 'estimated_size_end', 'estimated_quantity']
          if (needToFormatInCurrencyKeys.includes(key)) {
            oldValue = currencyFormatter(old[key])
            newValue = currencyFormatter(attributes[key])
          }

          if (needToFormatInDecimalKeys.includes(key)) {
            oldValue = decimalFormatter(old[key])
            newValue = decimalFormatter(attributes[key])
          }

          let translatedKey = this.$t(`sales_plans.${key}`)
          if (key === 'term_of_payment') {
            translatedKey = this.$t(`sales_plans.ToP`)
          } else if (key === 'partner_name') {
            translatedKey = this.$t(`sales_plans.partner`)
          } else if (key === 'farm_name') {
            translatedKey = this.$t(`sales_plans.farm`)
          } else if (key === 'processing_name') {
            translatedKey = this.$t(`sales_plans.processing`)
          } else if (key === 'product_name') {
            translatedKey = this.$t(`sales_plans.product`)
          }

          return `<span class="me-2" style="color: #667085">${this.$moment.utc(salesPlanActivityLog.created_at).local().format('DD-MM-YYYY HH:mm')}</span>[${this.lodash.startCase(translatedKey)}] ${this.$t('updated')}: <b class="text-danger">${oldValue}</b> -> <b class="text-success">${newValue}</b> <span style="color:#667085">${byText} ${causer_name}</span>`
        }).filter((val => val !== null))
      }

      return [`<span class="me-2" style="color: #667085">${this.$moment.utc(salesPlanActivityLog.created_at).local().format('DD-MM-YYYY HH:mm')}</span>${this.lodash.startCase(this.$t('sales_plans.singular'))} <b class="text-success">${createdText}</b> <span style="color:#667085">${byText} ${causer_name}</span></span>`]
    },
    openDeleteConfirmModal() {
      this.$set(this, 'deleteConfirmModalShow', true)
    },
    closeDeleteConfirmModal() {
      this.$set(this, 'deleteConfirmModalShow', false)
    },
    deleteConfirmDescription() {
      const description = this.$t('sales_plans.modal_messages.{something}_confirm_delete_description')
      const safeSomething = this.lodash.escape(this.$route.params.key)

      return description.replace('{something}', `<strong>${safeSomething}</strong>`)
    },
    buildActionPayload({cashflow = null, description = null, reason = null, file = null} = {}) {
      const payload = {}

      if (cashflow !== null && cashflow !== undefined) {
        payload.cashflow = cashflow
      }

      const resolvedText = [description, reason].find((value) => {
        return value !== null && value !== undefined && `${value}`.trim() !== ''
      })

      if (resolvedText !== undefined) {
        const text = `${resolvedText}`.trim()
        payload.description = text
        payload.reason = text
      }

      if (file) {
        payload.file = file
      }

      if (Object.keys(payload).length === 0) {
        return {}
      }

      return serialize(payload, {indices: true, booleansAsIntegers: true})
    },
    confirmDelete() {
      this.$set(this, 'deleting', true)

      return this.deleteSalesPlan()
          .then(() => {
            this.closeDeleteConfirmModal()
            return this.$router.push('/sales_plans')
          })
          .catch((error) => this.failedAlert(error))
          .finally(() => {
            this.$set(this, 'deleting', false)
          })
    },
    approveSalesPlan(payload = {}) {
      return this.$axios.post(
          `/v1/sales-plans/${this.$route.params.key}/approve`,
          this.buildActionPayload(payload)
      )
    },
    rejectSalesPlan(payload = {}) {
      return this.$axios.post(
          `/v1/sales-plans/${this.$route.params.key}/reject`,
          this.buildActionPayload(payload)
      )
    },
    showStatusSuccessAlert(messageKey) {
      return Swal.fire({
        title: this.$t('Success') + '!',
        text: this.$t(messageKey, {
          something: this.$route.params.key,
        }),
        type: 'success',
        confirmButtonClass: 'btn btn-success btn-fill',
        buttonsStyling: false
      })
    },
    handleApprove() {
      return this.$refs['sales-plan-approve-confirmation']
          .show({
            approvalLevel: this.model?.current_approval_level
                ?? this.model?.approval_progress?.current_level,
          })
          .then((result) => {
            if (!result) return null

            this.$set(this, 'approving', true)
            return this.approveSalesPlan(result)
                .then(() => this.fetchData())
                .then(() => this.showStatusSuccessAlert('sales_plans.modal_messages.approve_success_message'))
                .catch((error) => this.failedAlert(error))
                .finally(() => {
                  this.$set(this, 'approving', false)
                })
          })
    },
    handleReject() {
      return this.$refs['sales-plan-reject-confirmation']
          .show()
          .then((result) => {
            if (!result || !result.description) return null

            this.$set(this, 'rejecting', true)
            return this.rejectSalesPlan(result)
                .then(() => this.fetchData())
                .then(() => this.showStatusSuccessAlert('sales_plans.modal_messages.reject_success_message'))
                .catch((error) => this.failedAlert(error))
                .finally(() => {
                  this.$set(this, 'rejecting', false)
                })
          })
    },
    handleCancel() {
      return this.$refs['sales-plan-cancel-confirmation'].show(this.model)
    },
    onCancelled() {
      return this.fetchData()
    },
    openImageInNewTab(imageUrl) {
      window.open(imageUrl, '_blank');
    },
    openActivityLogModal() {
      this.$set(this, 'activityLogModalShow', true)
    },
    customerLabelFromBankAccount(bankAccount) {
      const {customer} = bankAccount || {}
      if (!customer) {
        return '-'
      }
      return customer?.name
    },
    bankAccountLabel(bankAccount) {
      if (!bankAccount) return '-'
      const {
        bank,
        customer,
        account_number,
        name,
      } = bankAccount || {}

      if (name) {
        return `${name} - ${account_number} - ${bank?.name}`
      }
      return `${customer?.name} - ${account_number} - ${bank?.name}`
    },
    showFailedToFetchModal() {
      this.$refs.salesPlanLoadErrorDialog.show(this.$route.params.key)
    },
    retryFailedToFetchModal() {
      this.fetchData()
    },
    showTimeoutRequestModal() {
      this.$set(this, 'timeoutRequestModalShow', true)
    },
    closeTimeoutRequestModal() {
      this.$set(this, 'timeoutRequestModalShow', false)
    },
    retryTimeoutRequestModal() {
      this.closeTimeoutRequestModal()
      this.fetchData()
    },
    getModelName() {
      return this.model["order_id"];
    },
    fetchData() {
      this.$set(this, 'loading', true)
      return Promise.all([
        this.fetchModel(),
        this.fetchModelActivityLogs()
      ]).then(([res, activityLogRes]) => {
        let {data: {data: data}} = res
        let {data: {data: activityLogData}} = activityLogRes

        this.setModelFromFetchData(data)
        this.setActivityLogs(activityLogData)
      }).catch((error) => {
        if (error.code === 'ECONNABORTED') {
          this.showTimeoutRequestModal()
        } else {
          this.showFailedToFetchModal()
        }
      }).finally(() => {
        this.$set(this, 'loading', false)
      });
    },
    fetchModel() {
      return this.$axios
          .get(`/v1/sales-plans/${this.$route.params.key}`, {
            params: {
              include: [
                'approval_template.approval_template_steps.approver',
                'creator',
                'farm',
                'partner',
                'processing',
                'product',
              ].join(',')
            },
            timeout: 30000,
          })
          .then((response) => {
            const {data: {data}} = response;
            this.$set(this, 'model', data)
            return response;
          });
    },
    fetchModelActivityLogs() {
      return this.$axios.get(`/v1/sales-plans/${this.$route.params.key}/activity-logs`, {
        params: {
          per_page: 1000,
        },
        timeout: 30000,
      })
          .then(res => {
            return res
          })
    },
    setModelFromFetchData(data) {
      this.$set(this, 'model', data)
    },
    setActivityLogs(data) {
      this.$set(this, 'activity_logs', data)
    }
  },
};
</script>

<style lang="scss" scoped>
::v-deep {
  .preloader-component {
    background-color: #ffffff;
  }

}

.approval-history-header {
  margin-bottom: 0;
  font: 600 14px/20px Inter !important;
  color: #6C7589 !important;
}

.badge.processing-dpd-badge {
  font-size: 12px;
  line-height: 16px;
  padding: 4px;
}

.processing-information {
  font: 400 14px/20px Inter;
}

.pbarik-progress-section {
  padding-top: 8px;
}

.pbarik-progress-title {
  font: 600 13px/18px Inter;
  color: #161B4D;
  text-transform: uppercase;
  letter-spacing: 0.02em;
}

.processing-limit-progress-meta {
  margin-top: 2px;
  font: 400 12px/18px Inter;
  color: #6C7589;
}

a.activity-log-collapse {
  color: #1664D8;
  font-weight: bold;
}

.change-history-card-text {
  padding: 16px;
  border-radius: 8px;
  background-color: #F8FAFC;
}

.change-history-title {
  font: 600 14px/20px Inter;
}

.container-title {
  margin-top: 10px;
  font-family: Lato;
  font-size: 12px;
  font-weight: 800;
  line-height: 14px;
  letter-spacing: 0.05em;
  text-align: left;
  margin-bottom: 8px;
  text-transform: uppercase;
  font-family: 'Lato';
  color: #6C7589;
}

.activity-log-modal-content {
  max-height: 60vh;
  overflow-y: auto;
}

.label {
  font-weight: 700;
  color: #161b4d;
  text-transform: capitalize;
  margin-bottom: 10px;
}

::v-deep .page-content {
  background: #eef3f7;
}

::v-deep {
  table {
    thead {
      background: #F9FAFB !important;
    }
  }
}

@import "../../../design/custom/components/status-badge";
</style>

<style lang="scss">
.modal-title.activity-log-modal-title {
  font: 600 16px/20px Inter;
  color: #1664D8 !important;
}
</style>
